import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { compareDesc, parse } from "date-fns";
import makeAjaxRequest from "helpers/makeAjaxRequest";
import { rootUrl } from "../actions/constants";

const initialState = {
  data: [],
  loading: false,
  error: null,
  selectedRow: null, // Add selectedRow to the initial state
  packageDetails: null
};

const grantsPackageState = state => state.grantsPackage;

export const selectedRowSelector = createSelector(
  [grantsPackageState],
  (grantsPackageState) => grantsPackageState.selectedRow
);

export const grantsPackageSelector = createSelector(
  [grantsPackageState],
  (grantsPackageState) => grantsPackageState.data
);

export const packageDetailsSelector = createSelector(
  [grantsPackageState],
  (grantsPackageState) => grantsPackageState.packageDetails
);

const sortBrowserData = (data) => {
  const sortedData = data.sort((a, b) => {
    if (a.packageCode === b.packageCode) {
      return 0;
    }
    return a.packageCode < b.packageCode ? -1 : 1;
  });

  return sortedData;
};

// Define the fetchGrantsPackage thunk
export const fetchGrantsPackage = createAsyncThunk(
  "grantsPackage/fetchGrantsPackage",
  async (rosterId, { rejectWithValue }) => {
    try {
      const data = await makeAjaxRequest.fetchWithAuth(`${rootUrl}/admin/laps/package/browse`);
      const sortedData = sortBrowserData(data);
      if (rosterId) {
        const index = sortedData.findIndex(obj => obj.rosterId === rosterId);
        if (index !== -1) {
          const [selectedObj] = sortedData.splice(index, 1);
          sortedData.unshift(selectedObj);
        }
      }
      return sortedData;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchPackageDetails = createAsyncThunk(
  "grantsPackage/fetchPackageDetails",
  async ({ rosterId }, { rejectWithValue }) => {
    // if packageId is not provided, return an empty object
    if (!rosterId) {
      return {};
    }
    try {
      const response = await makeAjaxRequest.fetchWithAuth(`${rootUrl}/admin/laps/package/${rosterId}/details`);
      if (Array.isArray(response) && response.length > 0) {
        // sort the response by budgetPdStartDate
        // date format is 'dd-MMM-YYYY'
        response.sort((a, b) => {
          return compareDesc(
            parse(a.budgetPdStartDate, "dd-MMM-yyyy", new Date()),
            parse(b.budgetPdStartDate, "dd-MMM-yyyy", new Date())
          );
        });

        return response;
      }
      return [];
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const grantsPackageSlice = createSlice({
  name: "grantsPackage",
  initialState,
  reducers: {
    saveSelectedRow: (state, action) => {
      state.selectedRow = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGrantsPackage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGrantsPackage.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchGrantsPackage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      .addCase(fetchPackageDetails.fulfilled, (state, action) => {
        state.packageDetails = action.payload;
        state.loading = false;
        state.error = null;
      });
  }
});

export const { saveSelectedRow } = grantsPackageSlice.actions;
export default grantsPackageSlice.reducer;
